import { EDatasourceType } from "../../index";

export interface IDatasourceDto {
  id: string;
  name: string;
  datasourceType: EDatasourceType;

  connectionString: string;
  code: string;
  restApiConnectionRequest: IRestServiceConnectionDto;
  oDataConnectionRequest: IODataConnectionDto;
  bluetoothInfo: IBluetoothInfo;
  mqttConnectionInfo: IMQTTConnectionInfo;
  isStaleRestApiClient?: boolean;

  /**
   * Multi-file project source for project-based backend-language datasources
   * (PROJECT_DATASOURCE_TYPES — PythonAction / JavaAction). Absent for the legacy
   * single-`code`-string datasources (JavaScriptAction / CSharpFunctions), which keep using `code`.
   * See docs/multi-language-backend-datasources-spec.md.
   */
  project?: IDatasourceProject;

  /**
   * Runtime version pin for a single-file backend-language datasource (PythonAction / JavaAction) that
   * uses `code` rather than a `project` tree. The multi-file `project` carries its own `runtime` instead.
   */
  runtime?: IDatasourceRuntimePin;
}

/** A single text file within a project-based datasource's source tree. */
export interface IDatasourceProjectFile {
  /** POSIX-relative path from the project root. Forward slashes only (cross-platform). */
  path: string;
  /** UTF-8 text content. */
  content: string;
}

/**
 * The runtime version a backend-language datasource is pinned to. The designer pins a *logical* version
 * (the user picks "CPython 3.11.9", not a per-platform build); codegen/deploy resolves it to the exact
 * per-RID archive hash in the content-addressed store for the deploy target. The language itself is
 * carried by `IDatasourceDto.datasourceType`.
 */
export interface IDatasourceRuntimePin {
  /** Logical runtime version key, e.g. "cpython-3.11.9" — resolved to a per-RID archive hash at build/deploy. */
  versionKey: string;
  /** Human-readable label for the designer, e.g. "CPython 3.11.9", "Temurin 8u402". */
  displayVersion: string;
}

/** Source + runtime pin + dependency lock for a project-based datasource. */
export interface IDatasourceProject {
  files: IDatasourceProjectFile[];
  runtime: IDatasourceRuntimePin;
  /** Lockfile content captured at the last successful design-time dependency resolution. */
  dependencyLock?: string;
}

export interface IRestApiPathExecuteDto {
  restApiConnectionRequest: IRestServiceConnectionDto;
  operationName: string;
}

export interface IRestServiceConnectionDto {
  prefix: string;
  swaggerUrl: string;
  serviceBaseUrl: string;
  clientIdKeyName: string;
  clientId: string;
  clientSecretKeyName: string;
  clientSecret: string;
  currentUserEmailName: string;
  authType: EAuthenticationType;
  extraHeaderParameters: { [key: string]: string };
  paths?: IRestApiEndpoint[];
  schemas?: IRestApiSchema[];
  description?: string;
}

export interface IODataConnectionDto {
  prefix: string;
  metadataUrl: string;
  serviceBaseUrl: string;
  clientIdKeyName: string;
  clientId: string;
  clientSecretKeyName: string;
  clientSecret: string;
  currentUserEmailName: string;
  authType: EAuthenticationType;
  extraHeaderParameters: { [key: string]: string };
  paths?: IRestApiEndpoint[];
  schemas?: IRestApiSchema[];
  description?: string;
}

export enum EAuthenticationType {
  NoAuth = 0,
  ClientIdSecret = 1,
  KuikaAuth = 2,
  InheritFromCollection = 3,
  Basic = 4
}

export interface IBluetoothInfo {
  id: string;
  name: string;
  address: string;
}

export enum EMQTTProtocolType {
  WebSocket = 0,
  MQTT = 1
}
export interface IMQTTConnectionInfo {
  validateCertificate: boolean;
  useTls: boolean;
  protocol: EMQTTProtocolType;
  port: number;
  basePath?: string;
  username?: string;
  password?: string;
  host?: string;
}

export const enum DefaultKuikaTableNames {
  KDevice = "KDevice",
  KDomain = "KDomain",
  KLog = "KLog",
  KNotification = "KNotification",
  KNotificationTemplate = "KNotificationTemplate",
  KRefreshTokens = "KRefreshTokens",
  KResetPasswordToken = "KResetPasswordToken",
  KResource = "KResource",
  KRole = "KRole",
  KUserNotification = "KUserNotification",
  KUserPasswordHistory = "KUserPasswordHistory",
  KUserRole = "KUserRole",
  KUserStripe = "KUserStripe",
  KEmailConfirmation = "KEmailConfirmation",
  KUser = "KUser",
  KGpsTracking = "KGpsTracking",
  KQueue = "KQueue"
}

export interface IRestApiEndpoint {
  path: string;
  operations: IRestApiEndpointOperation[];
}

export interface IRestApiEndpointOperation {
  operationName: string;
  type: HTTPMethod;
  parameters: [];
  request: IRestApiEndpointOperationRequest;
  responses: IRestApiEndpointOperationResponse[];
}

export interface IRestApiEndpointOperationRequest {
  schema?: string | null;
  type?: "array" | "object";
  clientIdKeyName: string;
  clientId: string;
  clientSecretKeyName: string;
  clientSecret: string;
  currentUserEmailName: string;
  authType: EAuthenticationType;
  extraHeaderParameters: { [key: string]: string };
  body: string;
}

export interface IRestApiEndpointOperationResponse {
  statusCode?: number;
  schema?: string | null;
  type?: "array" | "object";
  errorMessage?: string;
  body: string;
  isBodyEdited: boolean;
}

export interface IRestApiEndpointParameter {
  key?: string;
  value?: string;
  type?: RestApiEndpointParameterType;
  location?: RestApiEndpointParameterLocation;
}

export interface IRestApiSchema {
  name: string;
  fields: [
    {
      name: string;
      type: "string" | "number" | "boolean" | "array" | "object";
      mappedType: string;
      sampleValue: string;
    }
  ];
}

export type HTTPMethod = "GET" | "PUT" | "POST" | "DELETE";

export type RestApiEndpointParameterType = "string" | "number" | "boolean" | "array" | "object";

export type RestApiEndpointParameterLocation = "Path" | "Header" | "QueryString";
